import React, { useEffect, useState } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Library, ShieldAlert, AlertTriangle, ClipboardCheck, Radar, TrendingUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ModulePage";

const C = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

function Kpi({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card testId={testId} className="flex items-start gap-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/20 text-primary-foreground"><Icon className="h-5 w-5 text-accent" /></div>
      <div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  const riskData = Object.entries(d.by_risk).map(([name, value]) => ({ name, value }));
  const statusData = Object.entries(d.by_status).map(([name, value]) => ({ name, value }));
  const raCtx = `Executive dashboard. ${d.total_use_cases} use cases. Security tests: ${d.security_tests.passed} passed, ${d.security_tests.failed} failed. Open risks: ${d.open_risks}. Open SOC alerts: ${d.soc_open_alerts}. Audit controls failing: ${d.audit_controls.failed} of ${d.audit_controls.total}. Top value opportunity: ${d.top_value[0]?.name}.`;

  return (
    <div>
      <PageHeader title="Executive Dashboard" subtitle="AI portfolio, value, risk, security, compliance and assurance at a glance." readAloudContext={raCtx} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi icon={Library} label="Use Cases" value={d.total_use_cases} testId="kpi-usecases" />
        <Kpi icon={ShieldAlert} label="Security Tests Failed" value={d.security_tests.failed} sub={`${d.security_tests.total} total`} testId="kpi-sec" />
        <Kpi icon={Radar} label="Open SOC Alerts" value={d.soc_open_alerts} testId="kpi-soc" />
        <Kpi icon={AlertTriangle} label="Open Risks" value={d.open_risks} testId="kpi-risk" />
        <Kpi icon={ClipboardCheck} label="Controls Failing" value={d.audit_controls.failed} sub={`${d.audit_controls.total} total`} testId="kpi-ctrl" />
        <Kpi icon={TrendingUp} label="Red-Team Fails" value={d.red_team.failed} sub={`${d.red_team.total} missions`} testId="kpi-rt" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card testId="chart-risk">
          <h3 className="mb-4 text-sm font-semibold">Use Cases by Risk Level</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={riskData} dataKey="value" nameKey="name" outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                {riskData.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card testId="chart-status">
          <h3 className="mb-4 text-sm font-semibold">Use Cases by Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
              <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="mt-6" testId="top-value">
        <h3 className="mb-4 text-sm font-semibold">Top Business Value Opportunities <span className="text-xs font-normal text-muted-foreground">(SIMULATED estimates)</span></h3>
        <ul className="space-y-2">
          {d.top_value.map((t) => (
            <li key={t.id} className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 px-4 py-2.5 text-sm">
              <span className="font-medium">{t.name}</span>
              <span className="font-mono text-accent">${t.value.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
